// API client for TCO backend
// This will be replaced with generated client from @tco/api-client package

export interface ApiResponse<T = any> {
  data: T;
  message?: string;
  status: 'success' | 'error';
}

export interface PaginatedResponse<T = any> {
  data: T[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  activeCount?: number;
  from1cCount?: number;
  localCount?: number;
}

export interface ApiError {
  message: string;
  status: number;
  code?: string;
}


import AuthService from '../services/AuthService';
import envConfig from '../config/env';
import type { OneCSync_Response } from '../types/onec-sync.types';
import type { DamuMedConnectionStatus } from '../types/tenant.types';
import type { OneCStockResponse } from '../types/onec-stock.types';
import type {
  MarkirovkaStockResponse,
  MarkirovkaGtinMarksResponse,
  MarkirovkaStockSyncRun,
  MarkirovkaIncomingDocument,
  MarkirovkaIncomingDocumentsList,
  MarkirovkaDocumentMarksResponse,
  MarkirovkaDocumentFilters,
  MarkirovkaReconSession,
  MarkirovkaReconSessionsList,
  MarkirovkaReconSessionFilters,
  MarkirovkaReconPreview,
  MarkirovkaReconCisList,
  MarkirovkaCisKind,
  MarkirovkaDiscrepanciesList,
  MarkirovkaDiscrepancyFilters,
  MarkirovkaOpenSessionPayload,
  MarkirovkaUpdateSessionPayload,
} from '../types/markirovka-stock.types';
import type {
  StockRequisition,
  PaginatedStockRequisitions,
  StockRequisitionFilters,
  CreateStockRequisitionPayload,
  MarkReceivedPayload,
  ApproveStockRequisitionPayload,
  RejectStockRequisitionPayload,
} from '../types/stock-requisitions.types';
import type {
  StockTransfer,
  PaginatedStockTransfers,
  StockTransferFilters,
  CreateStockTransferPayload,
} from '../types/stock-transfers.types';
import type {
  PharmaDeadStockFilters,
  PharmaDeadStockList,
  PharmaDimensions,
  PharmaExpiryRiskFilters,
  PharmaExpiryRiskList,
  PharmaKpis,
  PharmaKpisFilters,
  PharmaMovementsFilters,
  PharmaMovementsList,
  PharmaTurnoverFilters,
  PharmaTurnoverList,
} from '../types/pharma-analytics.types';
import type {
  BioAgent,
  BioAgentWithToken,
  BioTerminal,
  CreateBioAgentDto,
  CreateBioTerminalDto,
  UpdateBioAgentDto,
  UpdateBioTerminalDto,
  BioTerminalCardsFilters,
  BioTerminalCardsResponse,
  MapBioTerminalCardResult,
} from '../types/bio.types';

// Base API configuration
const API_BASE_URL = envConfig.getApiBaseUrl();

class ApiClient {
  public baseURL: string;
  private authService: AuthService;

  constructor(baseURL: string = API_BASE_URL) {
    this.baseURL = baseURL;
    this.authService = AuthService.getInstance();
  }

  async getHeaders(): Promise<HeadersInit> {
    const token = await this.authService.getValidAccessToken();
    const tenantId = this.authService.getTenantId();

    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
      ...(tenantId && { 'X-Tenant-Id': tenantId }),
    };
  }

  private buildQueryString(params?: Record<string, any>): string {
    if (!params) return '';

    const searchParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        searchParams.append(key, String(value));
      }
    });

    return searchParams.toString();
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    isRetry: boolean = false
  ): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;

    // Get authentication headers
    const token = await this.authService.getValidAccessToken();
    const tenantId = this.authService.getTenantId();

    // If we don't have a valid token or tenant ID, redirect to login
    // (except for public endpoints like /health)
    if (!endpoint.startsWith('/health') && (!token || !tenantId)) {
      console.warn('[ApiClient] No valid token or tenant ID available, redirecting to login');
      await this.authService.logout();
      window.location.href = '/login';
      throw {
        message: 'Session expired. Please login again.',
        status: 401,
      } as ApiError;
    }

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
      ...(tenantId && { 'X-Tenant-Id': tenantId }),
      ...options.headers,
    };

    const config: RequestInit = {
      ...options,
      headers,
    };

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        // Handle 401 unauthorized - try to refresh token and retry once
        if (response.status === 401) {
          // Only retry once to prevent infinite loops
          if (!isRetry) {
            console.warn('[ApiClient] Received 401, attempting to refresh token and retry...');

            try {
              // Force token refresh
              const newToken = await this.authService.getValidAccessToken();

              if (newToken) {
                console.log('[ApiClient] Token refreshed successfully, retrying request...');
                // Retry the request with the new token
                return await this.request<T>(endpoint, options, true);
              }
            } catch (refreshError) {
              console.error('[ApiClient] Token refresh failed:', refreshError);
              // Fall through to logout
            }
          } else {
            console.error('[ApiClient] Retry with refreshed token also failed with 401');
          }

          // If we're here, either retry failed or token refresh failed
          console.warn('[ApiClient] Session expired, redirecting to login');
          await this.authService.logout();
          window.location.href = '/login';
          throw {
            message: 'Session expired. Please login again.',
            status: 401,
          } as ApiError;
        }

        const errorData = await response.json().catch(() => ({}));
        console.error('API Error Response:', {
          status: response.status,
          errorData,
          url: response.url
        });
        console.error('Full error details:', JSON.stringify(errorData, null, 2));
        throw {
          message: errorData.message || `HTTP error ${response.status}`,
          status: response.status,
          code: errorData.code,
        } as ApiError;
      }

      // Для DELETE запросов с кодом 204 тело ответа пустое
      if (response.status === 204) {
        return null as unknown as T;
      }

      // Проверяем, есть ли контент в ответе
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const data = await response.json();
        return data;
      } else {
        // Если контента нет или он не JSON, возвращаем пустой объект
        return {} as T;
      }
    } catch (error) {
      if (error instanceof TypeError) {
        throw {
          message: 'Network error - please check your connection',
          status: 0,
        } as ApiError;
      }
      throw error;
    }
  }

  // Health check
  async getHealth(): Promise<{ status: string; timestamp: string }> {
    return this.request('/health');
  }

  // Users API
  async getUsers(params?: Record<string, any>): Promise<PaginatedResponse<any>> {
    const searchParams = this.buildQueryString(params);
    return this.request(`/api/v1/users${searchParams ? `?${searchParams}` : ''}`);
  }

  async getUser(id: string): Promise<ApiResponse<any>> {
    return this.request(`/api/v1/users/${id}`);
  }

  async createUser(data: any): Promise<ApiResponse<any>> {
    return this.request('/api/v1/users', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateUser(id: string, data: any): Promise<ApiResponse<any>> {
    return this.request(`/api/v1/users/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteUser(id: string): Promise<ApiResponse<any> | null> {
    return this.request(`/api/v1/users/${id}`, {
      method: 'DELETE',
    });
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<{ message: string }> {
    return this.request('/api/v1/users/me/change-password', {
      method: 'PATCH',
      body: JSON.stringify({
        currentPassword,
        newPassword,
      }),
    });
  }

  async resetUserPassword(userId: string, newPassword: string): Promise<{ message: string }> {
    return this.request(`/api/v1/users/${userId}/reset-password`, {
      method: 'PATCH',
      body: JSON.stringify({
        newPassword,
      }),
    });
  }

  // Employees API
  async getEmployees(params?: Record<string, any>): Promise<PaginatedResponse<any>> {
    const searchParams = this.buildQueryString(params);
    return this.request(`/api/v1/employees${searchParams ? `?${searchParams}` : ''}`);
  }

  async getEmployee(id: string): Promise<ApiResponse<any>> {
    return this.request(`/api/v1/employees/${id}`);
  }

  async createEmployee(data: any): Promise<any> {
    return this.request('/api/v1/employees', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateEmployee(id: string, data: any): Promise<ApiResponse<any>> {
    return this.request(`/api/v1/employees/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteEmployee(id: string): Promise<ApiResponse<any> | null> {
    return this.request(`/api/v1/employees/${id}`, {
      method: 'DELETE',
    });
  }

  async createInvitationForEmployee(employeeId: string, roleId: string): Promise<{
    invitation: {
      id: string;
      invite_code: string;
      expires_at: string;
      is_valid: boolean;
    };
  }> {
    return this.request(`/api/v1/employees/${employeeId}/create-invitation`, {
      method: 'POST',
      body: JSON.stringify({ role_id: roleId }),
    });
  }

  // Departments API
  async getDepartments(params?: Record<string, any>): Promise<PaginatedResponse<any>> {
    const searchParams = this.buildQueryString(params);
    return this.request(`/api/v1/departments${searchParams ? `?${searchParams}` : ''}`);
  }

  async getDepartment(id: string): Promise<ApiResponse<any>> {
    return this.request(`/api/v1/departments/${id}`);
  }

  async createDepartment(data: any): Promise<ApiResponse<any>> {
    return this.request('/api/v1/departments', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateDepartment(id: string, data: any): Promise<ApiResponse<any>> {
    return this.request(`/api/v1/departments/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteDepartment(id: string): Promise<ApiResponse<any> | null> {
    return this.request(`/api/v1/departments/${id}`, {
      method: 'DELETE',
    });
  }

  // Positions API
  async getPositions(params?: Record<string, any>): Promise<PaginatedResponse<any>> {
    const searchParams = this.buildQueryString(params);
    return this.request(`/api/v1/positions${searchParams ? `?${searchParams}` : ''}`);
  }

  async getPosition(id: string): Promise<ApiResponse<any>> {
    return this.request(`/api/v1/positions/${id}`);
  }

  async createPosition(data: any): Promise<ApiResponse<any>> {
    return this.request('/api/v1/positions', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updatePosition(id: string, data: any): Promise<ApiResponse<any>> {
    return this.request(`/api/v1/positions/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deletePosition(id: string): Promise<ApiResponse<any> | null> {
    return this.request(`/api/v1/positions/${id}`, {
      method: 'DELETE',
    });
  }

  // Warehouses API
  async getWarehouses(params?: Record<string, any>): Promise<PaginatedResponse<any>> {
    const searchParams = this.buildQueryString(params);
    return this.request(`/api/v1/warehouses${searchParams ? `?${searchParams}` : ''}`);
  }

  async getWarehouse(id: string): Promise<ApiResponse<any>> {
    return this.request(`/api/v1/warehouses/${id}`);
  }

  // Roles API
  async getRoles(params?: Record<string, any>): Promise<PaginatedResponse<any>> {
    const searchParams = this.buildQueryString(params);
    return this.request(`/api/v1/roles${searchParams ? `?${searchParams}` : ''}`);
  }

  async getRole(id: string): Promise<any> {
    return this.request(`/api/v1/roles/${id}`);
  }

  async getRoleSections(): Promise<Array<{ key: string; label: string; medical: boolean }>> {
    return this.request('/api/v1/roles/sections');
  }

  async getMyPermissions(): Promise<any> {
    return this.request('/api/v1/roles/me/permissions');
  }

  async createRole(data: { name: string; permissions?: any }): Promise<any> {
    return this.request('/api/v1/roles', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateRole(id: string, data: { name?: string; permissions?: any }): Promise<any> {
    return this.request(`/api/v1/roles/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteRole(id: string): Promise<{ success: boolean }> {
    return this.request(`/api/v1/roles/${id}`, { method: 'DELETE' });
  }

  // Locations API
  async getLocations(params?: Record<string, any>): Promise<PaginatedResponse<any>> {
    const searchParams = this.buildQueryString(params);
    const url = `/api/v1/locations${searchParams ? `?${searchParams}` : ''}`;
    return this.request(url);
  }

  async getLocation(id: string): Promise<ApiResponse<any>> {
    return this.request(`/api/v1/locations/${id}`);
  }

  async createLocation(data: any): Promise<ApiResponse<any>> {
    return this.request('/api/v1/locations', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateLocation(id: string, data: any): Promise<ApiResponse<any>> {
    return this.request(`/api/v1/locations/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteLocation(id: string): Promise<ApiResponse<any> | null> {
    return this.request(`/api/v1/locations/${id}`, {
      method: 'DELETE',
    });
  }

  // Location QR API
  async getLocationQR(id: string, format?: string, size?: number): Promise<any> {
    const params: Record<string, string> = {};
    if (format) params.format = format;
    if (size) params.size = String(size);

    const searchParams = this.buildQueryString(params);
    return this.request(`/api/v1/locations/${id}/qr${searchParams ? `?${searchParams}` : ''}`);
  }

  async getLocationStatistics(id: string): Promise<ApiResponse<any>> {
    return this.request(`/api/v1/locations/${id}/statistics`);
  }

  // Employee Locations API
  async getLocationEmployees(locationId: string): Promise<ApiResponse<any[]>> {
    return this.request(`/api/v1/employee-locations/location/${locationId}`);
  }

  async getEmployeeLocations(employeeId: string): Promise<ApiResponse<any[]>> {
    return this.request(`/api/v1/employee-locations/employee/${employeeId}`);
  }

  async assignEmployees(data: any): Promise<ApiResponse<any>> {
    return this.request('/api/v1/employee-locations/assign', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async unassignEmployees(data: any): Promise<ApiResponse<any>> {
    return this.request('/api/v1/employee-locations/unassign', {
      method: 'DELETE',
      body: JSON.stringify(data),
    });
  }

  async updateAccessLevel(id: string, data: any): Promise<ApiResponse<any>> {
    return this.request(`/api/v1/employee-locations/${id}/access`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async checkAccess(employeeId: string, locationId: string): Promise<ApiResponse<any>> {
    return this.request(`/api/v1/employee-locations/check-access/${employeeId}/${locationId}`);
  }

  // Time Records API
  async getTimeRecords(params?: Record<string, any>): Promise<PaginatedResponse<any>> {
    const searchParams = this.buildQueryString(params);
    return this.request(`/api/v1/time-records${searchParams ? `?${searchParams}` : ''}`);
  }

  async getTimeRecordsByEmployee(employeeId: string, params?: Record<string, any>): Promise<PaginatedResponse<any>> {
    const searchParams = this.buildQueryString({ employeeId, ...params });
    return this.request(`/api/v1/time-records/by-employee${searchParams ? `?${searchParams}` : ''}`);
  }

  async aggregateTimeRecords(params: Record<string, any>): Promise<any> {
    const searchParams = this.buildQueryString(params);
    return this.request(`/api/v1/time-records/aggregate${searchParams ? `?${searchParams}` : ''}`);
  }

  async getMyTimeRecords(params?: Record<string, any>): Promise<PaginatedResponse<any>> {
    const searchParams = this.buildQueryString(params);
    return this.request(`/api/v1/time-records/my-records${searchParams ? `?${searchParams}` : ''}`);
  }

  async getMySummary(params?: Record<string, any>): Promise<any> {
    const searchParams = this.buildQueryString(params);
    return this.request(`/api/v1/time-records/my-summary${searchParams ? `?${searchParams}` : ''}`);
  }

  async generateTimeRecords(data: {
    start_date: string;
    end_date: string;
    employee_id?: string;
    employee_ids?: string[];
    overwrite?: boolean;
  }): Promise<{
    generated_count: number;
    updated_count: number;
    employees_processed: number;
    errors?: string[];
  }> {
    return this.request('/api/v1/time-records/generate', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Attendance Events API
  async getAttendanceEvents(params?: Record<string, any>): Promise<PaginatedResponse<any>> {
    const searchParams = this.buildQueryString(params);
    return this.request(`/api/v1/attendance-events${searchParams ? `?${searchParams}` : ''}`);
  }

  async createAttendanceEvent(data: any): Promise<ApiResponse<any>> {
    return this.request('/api/v1/attendance-events', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Sync Status API
  async getSyncStatus(syncType?: string): Promise<ApiResponse<any[]>> {
    const params: Record<string, string> = {};
    if (syncType) {
      params.sync_type = syncType;
    }
    const searchParams = this.buildQueryString(params);
    return this.request(`/api/v1/1c/sync-status${searchParams ? `?${searchParams}` : ''}`);
  }

  async getLatestSync(): Promise<ApiResponse<any>> {
    return this.request('/api/v1/1c/sync-status/latest');
  }

  /**
   * 1C Integration - Sync directories
   * Запускает полную синхронизацию справочников из 1С
   */
  async sync1CDirectories(): Promise<OneCSync_Response> {
    return this.request<OneCSync_Response>('/api/v1/1c/sync', {
      method: 'GET',
    });
  }

  // Schedule Templates API
  async get(endpoint: string, config?: { params?: Record<string, any> }): Promise<any> {
    const searchParams = this.buildQueryString(config?.params);
    return this.request(`${endpoint}${searchParams ? `?${searchParams}` : ''}`);
  }

  async post(endpoint: string, data?: any): Promise<any> {
    return this.request(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async patch(endpoint: string, data?: any): Promise<any> {
    return this.request(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete(endpoint: string): Promise<any> {
    return this.request(endpoint, {
      method: 'DELETE',
    });
  }

  // Tenant Settings API
  async getOrganizationInfo(): Promise<any> {
    return this.request('/api/v1/tenants/me/organization');
  }

  async getTenantSettings(): Promise<any> {
    return this.request('/api/v1/tenants/me/settings');
  }

  async updateTenantSettings(data: any): Promise<any> {
    return this.request('/api/v1/tenants/me/settings', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async generateQRSecret(): Promise<any> {
    return this.request('/api/v1/tenants/me/settings/qr-secret', {
      method: 'POST',
    });
  }

  // Superadmin (platform organizations management)
  async getSuperadminOrganizations(params: {
    search?: string;
    page?: number;
    limit?: number;
  } = {}): Promise<any> {
    const query = new URLSearchParams();
    if (params.search) query.set('search', params.search);
    if (params.page !== undefined) query.set('page', String(params.page));
    if (params.limit !== undefined) query.set('limit', String(params.limit));
    const qs = query.toString();
    return this.request(`/api/v1/superadmin/organizations${qs ? `?${qs}` : ''}`);
  }

  async getSuperadminOrganizationMetrics(id: string): Promise<any> {
    return this.request(`/api/v1/superadmin/organizations/${id}/metrics`);
  }

  async createSuperadminOrganization(data: any): Promise<any> {
    return this.request('/api/v1/superadmin/organizations', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateSuperadminOrganization(id: string, data: any): Promise<any> {
    return this.request(`/api/v1/superadmin/organizations/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async updateSuperadminOrganizationModules(
    id: string,
    data: any,
  ): Promise<any> {
    return this.request(`/api/v1/superadmin/organizations/${id}/modules`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  // Markirovka (edo.markirovka.kz) integration
  async testMarkirovkaConnection(data: {
    username?: string;
    password?: string;
    bin?: string;
    commodity_group?: string;
  }): Promise<{
    ok: boolean;
    bin: string;
    organization_name: string;
    states_km_count: number;
  }> {
    return this.request('/api/v1/markirovka/test-connection', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // DamuMed: состояние подключения (без обращения к DamuMed)
  async getDamuMedStatus(): Promise<DamuMedConnectionStatus> {
    return this.request('/api/v1/assignments/damumed/status');
  }

  // DamuMed: вход с сохранёнными учётными данными. force — войти даже во время паузы
  async testDamuMedConnection(force = false): Promise<DamuMedConnectionStatus> {
    return this.request('/api/v1/assignments/damumed/test-connection', {
      method: 'POST',
      body: JSON.stringify({ force }),
    });
  }

  // Остатки медикаментов из 1С (реальное время, с фильтром по складу)
  async getOneCStock(warehouseId?: string): Promise<OneCStockResponse> {
    const qs = warehouseId
      ? `?warehouseId=${encodeURIComponent(warehouseId)}`
      : '';
    return this.request(`/api/v1/1c/stock${qs}`);
  }

  // Остатки маркированных медикаментов (edo.markirovka.kz)
  async getMarkirovkaStock(): Promise<MarkirovkaStockResponse> {
    return this.request('/api/v1/markirovka/stock');
  }

  async getMarkirovkaGtinMarks(
    gtin: string,
  ): Promise<MarkirovkaGtinMarksResponse> {
    return this.request(
      `/api/v1/markirovka/stock/${encodeURIComponent(gtin)}/marks`,
    );
  }

  async triggerMarkirovkaStockSync(): Promise<{ accepted: boolean }> {
    return this.request('/api/v1/markirovka/stock/sync', { method: 'POST' });
  }

  // Заявки на запрос остатков (stock-requisitions)
  async getStockRequisitions(
    params?: StockRequisitionFilters,
  ): Promise<PaginatedStockRequisitions> {
    const qs = this.buildQueryString(params);
    return this.request(
      `/api/v1/stock-requisitions${qs ? `?${qs}` : ''}`,
    );
  }

  async getStockRequisition(id: string): Promise<StockRequisition> {
    return this.request(
      `/api/v1/stock-requisitions/${encodeURIComponent(id)}`,
    );
  }

  async createStockRequisition(
    data: CreateStockRequisitionPayload,
  ): Promise<StockRequisition> {
    return this.request('/api/v1/stock-requisitions', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async markStockRequisitionReceived(
    id: string,
    data: MarkReceivedPayload,
  ): Promise<StockRequisition> {
    return this.request(
      `/api/v1/stock-requisitions/${encodeURIComponent(id)}/mark-received`,
      { method: 'PATCH', body: JSON.stringify(data) },
    );
  }

  // Одобрение/отклонение обычно делает провизор в 1С; методы оставлены для полноты.
  async approveStockRequisition(
    id: string,
    data: ApproveStockRequisitionPayload,
  ): Promise<StockRequisition> {
    return this.request(
      `/api/v1/stock-requisitions/${encodeURIComponent(id)}/approve`,
      { method: 'PATCH', body: JSON.stringify(data) },
    );
  }

  async rejectStockRequisition(
    id: string,
    data: RejectStockRequisitionPayload,
  ): Promise<StockRequisition> {
    return this.request(
      `/api/v1/stock-requisitions/${encodeURIComponent(id)}/reject`,
      { method: 'PATCH', body: JSON.stringify(data) },
    );
  }

  // Перемещения между складами (stock-transfers)
  async getStockTransfers(
    params?: StockTransferFilters,
  ): Promise<PaginatedStockTransfers> {
    const qs = this.buildQueryString(params as Record<string, any>);
    return this.request(`/api/v1/stock-transfers${qs ? `?${qs}` : ''}`);
  }

  async getStockTransfer(id: string): Promise<StockTransfer> {
    return this.request(`/api/v1/stock-transfers/${encodeURIComponent(id)}`);
  }

  async createStockTransfer(
    data: CreateStockTransferPayload,
  ): Promise<StockTransfer> {
    return this.request('/api/v1/stock-transfers', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getMarkirovkaStockSyncLatest(): Promise<MarkirovkaStockSyncRun | null> {
    return this.request('/api/v1/markirovka/stock/sync/latest');
  }

  async getMarkirovkaDocuments(
    params?: MarkirovkaDocumentFilters,
  ): Promise<MarkirovkaIncomingDocumentsList> {
    const qs = this.buildQueryString(params as Record<string, any>);
    return this.request(
      `/api/v1/markirovka/incoming-documents${qs ? `?${qs}` : ''}`,
    );
  }

  async getMarkirovkaDocumentMarks(
    id: string,
  ): Promise<MarkirovkaDocumentMarksResponse> {
    return this.request(
      `/api/v1/markirovka/incoming-documents/${encodeURIComponent(id)}/marks`,
    );
  }

  async toggleMarkirovkaDocumentExcluded(
    id: string,
    isExcluded: boolean,
  ): Promise<MarkirovkaIncomingDocument> {
    return this.request(
      `/api/v1/markirovka/incoming-documents/${encodeURIComponent(id)}`,
      { method: 'PATCH', body: JSON.stringify({ is_excluded: isExcluded }) },
    );
  }

  // Сверка «учёт vs факт» — сессии инвентаризации (Этап 3)
  async getMarkirovkaReconSessions(
    params?: MarkirovkaReconSessionFilters,
  ): Promise<MarkirovkaReconSessionsList> {
    const qs = this.buildQueryString(params as Record<string, any>);
    return this.request(
      `/api/v1/markirovka/recon/sessions${qs ? `?${qs}` : ''}`,
    );
  }

  async getMarkirovkaReconSession(
    id: string,
  ): Promise<MarkirovkaReconSession> {
    return this.request(
      `/api/v1/markirovka/recon/sessions/${encodeURIComponent(id)}`,
    );
  }

  async openMarkirovkaReconSession(
    payload: MarkirovkaOpenSessionPayload = {},
  ): Promise<MarkirovkaReconSession> {
    return this.request('/api/v1/markirovka/recon/sessions', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async updateMarkirovkaReconSession(
    id: string,
    payload: MarkirovkaUpdateSessionPayload,
  ): Promise<MarkirovkaReconSession> {
    return this.request(
      `/api/v1/markirovka/recon/sessions/${encodeURIComponent(id)}`,
      { method: 'PATCH', body: JSON.stringify(payload) },
    );
  }

  async closeMarkirovkaReconSession(
    id: string,
  ): Promise<MarkirovkaReconSession> {
    return this.request(
      `/api/v1/markirovka/recon/sessions/${encodeURIComponent(id)}/close`,
      { method: 'POST' },
    );
  }

  async exportMarkirovkaReconSession(
    id: string,
  ): Promise<MarkirovkaReconSession> {
    return this.request(
      `/api/v1/markirovka/recon/sessions/${encodeURIComponent(id)}/export`,
      { method: 'POST' },
    );
  }

  async deleteMarkirovkaReconSession(id: string): Promise<void> {
    await this.request(
      `/api/v1/markirovka/recon/sessions/${encodeURIComponent(id)}`,
      { method: 'DELETE' },
    );
  }

  // «Заполнить учётный остаток»: синк edo + живой расчёт (admin).
  async syncMarkirovkaReconStock(
    id: string,
  ): Promise<MarkirovkaReconPreview> {
    return this.request(
      `/api/v1/markirovka/recon/sessions/${encodeURIComponent(id)}/sync-stock`,
      { method: 'POST' },
    );
  }

  // «Заполнить фактические остатки»: живой расчёт без закрытия.
  async getMarkirovkaReconPreview(
    id: string,
  ): Promise<MarkirovkaReconPreview> {
    return this.request(
      `/api/v1/markirovka/recon/sessions/${encodeURIComponent(id)}/preview`,
    );
  }

  // Поштучный список CIS документа (на вывод / излишек / в наличии).
  async getMarkirovkaReconCis(
    id: string,
    params: { kind: MarkirovkaCisKind; page?: number; limit?: number },
  ): Promise<MarkirovkaReconCisList> {
    const qs = this.buildQueryString(params as Record<string, any>);
    return this.request(
      `/api/v1/markirovka/recon/sessions/${encodeURIComponent(id)}/cis${qs ? `?${qs}` : ''}`,
    );
  }

  async getMarkirovkaReconDiscrepancies(
    id: string,
    params?: MarkirovkaDiscrepancyFilters,
  ): Promise<MarkirovkaDiscrepanciesList> {
    const qs = this.buildQueryString(params as Record<string, any>);
    return this.request(
      `/api/v1/markirovka/recon/sessions/${encodeURIComponent(id)}/discrepancies${qs ? `?${qs}` : ''}`,
    );
  }

  // ==================== NOTIFICATIONS API ====================

  // Device Tokens API
  async registerDeviceToken(data: any): Promise<any> {
    return this.request('/api/v1/device-tokens/register', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async unregisterDeviceToken(deviceId: string): Promise<any> {
    return this.request('/api/v1/device-tokens/unregister', {
      method: 'DELETE',
      body: JSON.stringify({ device_id: deviceId }),
    });
  }

  async getMyDevices(): Promise<ApiResponse<any[]>> {
    return this.request('/api/v1/device-tokens/my-devices');
  }

  // Notifications API
  async getNotifications(params?: Record<string, any>): Promise<PaginatedResponse<any>> {
    const searchParams = this.buildQueryString(params);
    return this.request(`/api/v1/notifications${searchParams ? `?${searchParams}` : ''}`);
  }

  async getNotification(id: string): Promise<ApiResponse<any>> {
    return this.request(`/api/v1/notifications/${id}`);
  }

  async createNotification(data: any): Promise<any> {
    return this.request('/api/v1/notifications', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateNotification(id: string, data: any): Promise<ApiResponse<any>> {
    return this.request(`/api/v1/notifications/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteNotification(id: string): Promise<ApiResponse<any> | null> {
    return this.request(`/api/v1/notifications/${id}`, {
      method: 'DELETE',
    });
  }

  async sendNotification(id: string): Promise<any> {
    return this.request(`/api/v1/notifications/${id}/send`, {
      method: 'POST',
    });
  }

  async getMyNotifications(params?: Record<string, any>): Promise<PaginatedResponse<any>> {
    const searchParams = this.buildQueryString(params);
    return this.request(`/api/v1/notifications/my-notifications${searchParams ? `?${searchParams}` : ''}`);
  }

  async markNotificationAsRead(id: string): Promise<any> {
    return this.request(`/api/v1/notifications/${id}/mark-as-read`, {
      method: 'PATCH',
    });
  }

  async markAllNotificationsAsRead(): Promise<any> {
    return this.request('/api/v1/notifications/mark-all-as-read', {
      method: 'PATCH',
    });
  }

  async getNotificationDeliveryStats(id: string): Promise<ApiResponse<any>> {
    return this.request(`/api/v1/notifications/${id}/delivery-stats`);
  }

  // Medications API
  async getMedicationScans(params?: Record<string, any>): Promise<PaginatedResponse<any>> {
    const searchParams = this.buildQueryString(params);
    return this.request(`/api/v1/medications/scans${searchParams ? `?${searchParams}` : ''}`);
  }

  async retryMedicationScanSync(scanId: string): Promise<ApiResponse<any>> {
    return this.request(`/api/v1/medications/scans/${scanId}/retry`, {
      method: 'POST',
    });
  }

  async retryPendingMedicationScans(): Promise<ApiResponse<{ total: number; retried: number; message: string }>> {
    return this.request('/api/v1/medications/scans/retry-pending', {
      method: 'POST',
    });
  }

  // Assignment Scans API
  async getAssignmentScans(params?: Record<string, any>): Promise<PaginatedResponse<any>> {
    const searchParams = this.buildQueryString(params);
    return this.request(`/api/v1/assignments/scans${searchParams ? `?${searchParams}` : ''}`);
  }

  // ==================== ORGANIZATIONS API ====================

  async getOrganizations(params?: Record<string, any>): Promise<PaginatedResponse<any>> {
    // Transform sortBy + sortOrder into sort (backend expects 'name:asc' format)
    const transformedParams = { ...params };
    if (transformedParams?.sortBy) {
      transformedParams.sort = `${transformedParams.sortBy}:${transformedParams.sortOrder || 'asc'}`;
      delete transformedParams.sortBy;
      delete transformedParams.sortOrder;
    }
    const searchParams = this.buildQueryString(transformedParams);
    return this.request(`/api/v1/organizations${searchParams ? `?${searchParams}` : ''}`);
  }

  async getOrganization(id: string): Promise<any> {
    return this.request(`/api/v1/organizations/${id}`);
  }

  async getOrganizationWithStats(id: string): Promise<any> {
    return this.request(`/api/v1/organizations/${id}/with-stats`);
  }

  async getOrganizationStats(id: string): Promise<any> {
    return this.request(`/api/v1/organizations/${id}/stats`);
  }

  async getOrganizationEmployees(id: string, params?: Record<string, any>): Promise<PaginatedResponse<any>> {
    const searchParams = this.buildQueryString(params);
    return this.request(`/api/v1/organizations/${id}/employees${searchParams ? `?${searchParams}` : ''}`);
  }

  async getHeadquartersOrganization(): Promise<any> {
    return this.request('/api/v1/organizations/headquarters');
  }

  async createOrganization(data: any): Promise<any> {
    return this.request('/api/v1/organizations', {
      method: 'POST',
    });
  }

  // Documents API (Admin endpoints - with employeeId) - POST requests for document generation
  async getPayslipDocument(employeeId: string, period: string): Promise<any> {
    return this.request(`/api/v1/documents/admin/payslip?employeeId=${employeeId}&period=${period}`, {
      method: 'POST',
    });
  }

  async getOrderDocument(employeeId: string, documentId: string): Promise<any> {
    return this.request(`/api/v1/documents/admin/order?employeeId=${employeeId}&documentId=${documentId}`, {
      method: 'POST',
    });
  }

  async getCertificateDocument(employeeId: string, documentId: string): Promise<any> {
    return this.request(`/api/v1/documents/admin/certificate?employeeId=${employeeId}&documentId=${documentId}`, {
      method: 'POST',
    });
  }

  async getContractDocument(employeeId: string): Promise<any> {
    return this.request(`/api/v1/documents/admin/contract?employeeId=${employeeId}`, {
      method: 'POST',
    });
  }

  async getMyDocuments(params?: Record<string, any>): Promise<PaginatedResponse<any>> {
    const searchParams = this.buildQueryString(params);
    return this.request(`/api/v1/documents/my-documents${searchParams ? `?${searchParams}` : ''}`);
  }

  // [ADMIN] Documents API - для администраторов
  async getAllDocumentsAdmin(params?: Record<string, any>): Promise<any> {
    const searchParams = this.buildQueryString(params);
    return this.request(`/api/v1/documents/admin/all-documents${searchParams ? `?${searchParams}` : ''}`);
  }

  async downloadDocumentAdmin(documentId: string): Promise<{ url: string; filename: string; expiresIn: number }> {
    // Получаем signed URL из backend
    return this.request(`/api/v1/documents/admin/documents/${documentId}/download`);
  }

  // ==================== REPORTS API ====================

  async getLatenessReport(params: { date: string; organizationId?: string; departmentId?: string }): Promise<any> {
    const searchParams = this.buildQueryString(params);
    return this.request(`/api/v1/reports/lateness${searchParams ? `?${searchParams}` : ''}`);
  }

  async getNoScheduleReport(params: { organizationId?: string; departmentId?: string }): Promise<any> {
    const searchParams = this.buildQueryString(params);
    return this.request(`/api/v1/reports/no-schedule${searchParams ? `?${searchParams}` : ''}`);
  }

  async getTimesheetReport(params: { year: number; month: number; organizationId?: string; departmentId?: string }): Promise<any> {
    const searchParams = this.buildQueryString(params);
    return this.request(`/api/v1/reports/timesheet${searchParams ? `?${searchParams}` : ''}`);
  }

  // ==================== PUBLIC REQUEST (no auth) ====================

  private async publicRequest<T>(
    endpoint: string,
    options: RequestInit = {},
  ): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    const config: RequestInit = {
      ...options,
      headers,
    };

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw {
          message: errorData.message || `HTTP error ${response.status}`,
          status: response.status,
          code: errorData.code,
        } as ApiError;
      }

      if (response.status === 204) {
        return null as unknown as T;
      }

      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return await response.json();
      }
      return {} as T;
    } catch (error) {
      if (error instanceof TypeError) {
        throw {
          message: 'Network error - please check your connection',
          status: 0,
        } as ApiError;
      }
      throw error;
    }
  }

  // ==================== LICENSING API (superadmin) ====================
  // Реестр лицензий кросс-тенантный и закрыт SuperAdminGuard на бэкенде.
  // Раньше вызовы шли через publicRequest (без Authorization) — это работало
  // только потому, что весь контроллер был помечен @Public.

  async getFeatures(params?: Record<string, any>): Promise<any> {
    const searchParams = this.buildQueryString(params);
    return this.request(`/api/v1/licensing/features${searchParams ? `?${searchParams}` : ''}`);
  }

  async createFeature(data: any): Promise<any> {
    return this.request('/api/v1/licensing/features', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateFeature(id: string, data: any): Promise<any> {
    return this.request(`/api/v1/licensing/features/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteFeature(id: string): Promise<any> {
    return this.request(`/api/v1/licensing/features/${id}`, {
      method: 'DELETE',
    });
  }

  async getLicenses(params?: Record<string, any>): Promise<any> {
    const searchParams = this.buildQueryString(params);
    return this.request(`/api/v1/licensing/licenses${searchParams ? `?${searchParams}` : ''}`);
  }

  async createLicense(data: any): Promise<any> {
    return this.request('/api/v1/licensing/licenses', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateLicense(id: string, data: any): Promise<any> {
    return this.request(`/api/v1/licensing/licenses/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteLicense(id: string): Promise<any> {
    return this.request(`/api/v1/licensing/licenses/${id}`, {
      method: 'DELETE',
    });
  }

  // ==========================================================
  // Фарм-аналитика (витрины по данным 1С)
  // ==========================================================

  private pharmaUrl(path: string, params?: Record<string, any>): string {
    const qs = this.buildQueryString(params);
    return `/api/v1/pharma-analytics${path}${qs ? `?${qs}` : ''}`;
  }

  async getPharmaKpis(params?: PharmaKpisFilters): Promise<PharmaKpis> {
    return this.request(this.pharmaUrl('/kpis', params));
  }

  async getPharmaExpiryRisk(
    params?: PharmaExpiryRiskFilters,
  ): Promise<PharmaExpiryRiskList> {
    return this.request(this.pharmaUrl('/expiry-risk', params));
  }

  async getPharmaDeadStock(
    params?: PharmaDeadStockFilters,
  ): Promise<PharmaDeadStockList> {
    return this.request(this.pharmaUrl('/dead-stock', params));
  }

  async getPharmaTurnover(
    params?: PharmaTurnoverFilters,
  ): Promise<PharmaTurnoverList> {
    return this.request(this.pharmaUrl('/warehouse-turnover', params));
  }

  async getPharmaMovements(
    params?: PharmaMovementsFilters,
  ): Promise<PharmaMovementsList> {
    return this.request(this.pharmaUrl('/movements', params));
  }

  async getPharmaDimensions(): Promise<PharmaDimensions> {
    return this.request(this.pharmaUrl('/dimensions'));
  }

  // Биометрические терминалы: агенты объектов и устройства
  async getBioAgents(): Promise<BioAgent[]> {
    return this.request('/api/v1/bio/agents');
  }

  async createBioAgent(data: CreateBioAgentDto): Promise<BioAgentWithToken> {
    return this.request('/api/v1/bio/agents', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateBioAgent(id: string, data: UpdateBioAgentDto): Promise<BioAgent> {
    return this.request(`/api/v1/bio/agents/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async rotateBioAgentToken(id: string): Promise<{ id: string; token: string }> {
    return this.request(`/api/v1/bio/agents/${id}/rotate-token`, {
      method: 'POST',
    });
  }

  async getBioTerminals(): Promise<BioTerminal[]> {
    return this.request('/api/v1/bio/terminals');
  }

  async createBioTerminal(data: CreateBioTerminalDto): Promise<BioTerminal> {
    return this.request('/api/v1/bio/terminals', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateBioTerminal(
    id: string,
    data: UpdateBioTerminalDto,
  ): Promise<BioTerminal> {
    return this.request(`/api/v1/bio/terminals/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async getBioTerminalCards(
    params?: BioTerminalCardsFilters,
  ): Promise<BioTerminalCardsResponse> {
    const searchParams = this.buildQueryString(params);
    return this.request(
      `/api/v1/bio/terminal-cards${searchParams ? `?${searchParams}` : ''}`,
    );
  }

  /** Сопоставление карточки с сотрудником + бэкфилл событий в табель. */
  async mapBioTerminalCard(
    cardId: string,
    employeeId: string,
  ): Promise<MapBioTerminalCardResult> {
    return this.request(`/api/v1/bio/terminal-cards/${cardId}/map`, {
      method: 'POST',
      body: JSON.stringify({ employee_id: employeeId }),
    });
  }

  /** Прямая команда терминалу: capture_face | sync_time | reboot. */
  async createBioCommand(data: {
    terminal_id: string;
    command_type: 'capture_face' | 'sync_time' | 'reboot';
    employee_id?: string;
  }): Promise<{ id: string; status: string }> {
    return this.request('/api/v1/bio/commands', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }
}

export const apiClient = new ApiClient();
export const api = apiClient;